<template>
  <v-sheet class="pa-4" :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'">
    <div class="d-flex align-center">
      <div class="d-flex align-center">
        <v-icon x-small class="mr-2" color="#039de9">mdi-circle</v-icon>
        <span>Total Clients</span>

        <v-tooltip top color="black">
          <template v-slot:activator="{ on, attrs }">
            <v-icon small class="ml-2" color="#708AA7" v-bind="attrs" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <span>Client registrations count for selected period</span>
        </v-tooltip>
      </div>

      <v-spacer></v-spacer>

      <v-select
        v-model="selectedDuration"
        :items="durationOptions"
        dense
        hide-details
        class="timeframe-select"
        style="max-width: 90px"
        :menu-props="{ offsetY: true }"
        append-icon="mdi-chevron-down"
        @change="handleDurationChange"
      />
    </div>

    <apexchart type="bar" height="155" :options="chartOptions" :series="series" />
  </v-sheet>
</template>

<script>
import { LOTCHART__ACTIONS } from "@/store/action-types";
import VueApexCharts from "vue-apexcharts";
import { mapActions, mapState } from "vuex";

export default {
  name: "ClientsChart",

  components: { apexchart: VueApexCharts },

  data() {
    return {
      selectedDuration: "weekly",

      durationOptions: [
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
        { text: "Yearly", value: "yearly" },
      ],

      // tooltip date map
      dateRanges: {},

      chartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          background: "transparent",
        },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: "30%" },
        },
        colors: ["#039de9"],
        grid: {
          borderColor: "#1A2C42",
          yaxis: { lines: { show: false } },
          xaxis: { lines: { show: false } },
        },
        xaxis: {
          categories: [],
          labels: { style: { colors: "#708AA7" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          show: false,
          labels: {
            style: { colors: "#708AA7" },
            formatter: (value) => this.formatValue(value),
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "dark",
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            const value = series[seriesIndex][dataPointIndex];
            const label = this.chartOptions.xaxis.categories[dataPointIndex];
            const dateRange = this.dateRanges[label] || {};

            return `
<div style="padding: 10px;">
  <div style="font-size: 11px; padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.1);">
    ${label}
  </div>
  <div style="font-size: 11px; padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.1); display:flex; align-items:center; gap:6px;">
    <div style="width:8px; height:8px; background-color:#039de9; border-radius:50%; animation:pulse 1s infinite;"></div>
    Total Clients: ${this.formatValue(value)}
  </div>
  <div style="font-size: 11px; padding: 4px 0;">
    ${this.formatDate(dateRange.startDate)} - ${this.formatDate(dateRange.endDate)}
  </div>
</div>
<style>
@keyframes pulse { 0%{opacity:1} 50%{opacity:.3} 100%{opacity:1} }
</style>
            `;
          },
        },
      },

      series: [
        {
          name: "Total Clients",
          data: [],
        },
      ],
    };
  },

  computed: {
    ...mapState("chart", ["chartData"]),
  },

  methods: {
    ...mapActions("chart", {
      fetchChartData: LOTCHART__ACTIONS.TRADINGLOTCHART,
    }),

    // for counts: no decimals
    formatValue(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
      return `${Math.round(n)}`;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    handleDurationChange() {
      this.fetchChartData({ duration: this.selectedDuration });
    },

    updateChartData() {
      const arr = this.chartData || [];
      if (!arr.length) {
        this.series = [{ name: "Total Clients", data: [] }];
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { ...this.chartOptions.xaxis, categories: [] },
        };
        this.dateRanges = {};
        return;
      }

      const categories = arr.map((x) => x.label);
      const data = arr.map((x) => Number(x.totalClients || 0));

      this.dateRanges = arr.reduce((acc, x) => {
        acc[x.label] = { startDate: x.startDate, endDate: x.endDate };
        return acc;
      }, {});

      this.series = [{ name: "Total Clients", data }];

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: { ...this.chartOptions.xaxis, categories },
      };
    },
  },

  watch: {
    chartData: {
      handler: "updateChartData",
      immediate: true,
    },
  },

  async mounted() {
    await this.fetchChartData({ duration: this.selectedDuration });
  },
};
</script>

<style scoped>
.timeframe-select ::v-deep .v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
}

.timeframe-select ::v-deep .v-select__selection,
.timeframe-select ::v-deep .v-icon {
  color: #708AA7;
  position: relative;
  top: -1px;
  right: 30px;
}

.timeframe-select ::v-deep .v-select__selection--comma {
  margin-right: 4px;
}

.timeframe-select ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}

.timeframe-select ::v-deep .v-input__control .v-input__slot:before,
.timeframe-select ::v-deep .v-input__control .v-input__slot:after {
  border-color: transparent !important;
}
</style>
